import type { BrandUserExpanded } from '@dashhudson/api-client/auth/model/raw';
import type { RouteLocationNormalized, Router } from 'vue-router';

import uniqBy from 'lodash/uniqBy';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type {
  AuthPendingState,
  Brand,
  BrandTag,
  BrandTagById,
  BrandUser,
  Identity,
  PermissionObject,
  RawBrandTag,
  RawIdentity,
} from '@/types/auth.types';

import * as AuthAPI from '@/apis/auth';
import { getHttpErrorDescription, getHttpErrorStatus, isHttpError } from '@/apis/http-errors';
import { env } from '@/env';
import { isSameCustomerPlan } from '@/models/auth/customer-plan.enum';
import { defaultErrorMessage, LoginMethodLabel } from '@/models/auth/login.enum';
import { guardRoute } from '@/models/auth/permissions.enum';
import { flattenBrandUserPermissions } from '@/models/auth/permissions.utils';
import { useIdentityStore } from '@/stores/identity';
import { useImpersonatorStore } from '@/stores/impersonator';
import { useTokenStore } from '@/stores/token';
import { AuthEventTracker } from '@/utils/analytics/auth-tracking';
import {
  browserStorageGetItem,
  browserStorageRemoveItem,
  browserStorageSetItem,
} from '@/utils/browserStorage';
import { logger } from '@/utils/logger';

import { useOneTimePasswordStore } from './one-time-password';

const TWITTER_OAUTH2_PROVIDER = 'twitter_oauth2';
const TWITTER_OAUTH2_PRODUCTION_CALLBACK_ORIGIN = 'https://app.dashsocial.co';

const track = new AuthEventTracker();

export const useAuthStore = defineStore('auth', () => {
  const tokenStore = useTokenStore();
  const identityStore = useIdentityStore();
  const impersonatorStore = useImpersonatorStore();
  const oneTimePasswordStore = useOneTimePasswordStore();

  /**
   * Below are passthrough to the identity store.  If you only require access to the
   * identity or currentBrand state, please use the identity store.
   *
   * At some point, we will look to remove these references in the future.
   */

  /** ************************************************************ */
  const identity = computed({
    get(): Identity | null {
      return identityStore.identity;
    },
    set(v: Identity | null) {
      identityStore.identity = v;
    },
  });
  const currentBrandLabel = computed({
    get(): string | null {
      return identityStore.currentBrandLabel;
    },
    set(v: string | null) {
      identityStore.currentBrandLabel = v;
    },
  });

  const isLoggedIn = computed(() => !!identityStore.identity);
  const shouldShowLoginAnimation = ref(false);
  const canOnlyAccessInactiveBrand = computed(() => identityStore.canOnlyAccessInactiveBrand);
  const currentBrand = computed(() => identityStore.currentBrand);
  // using identityStore.guard() is preferred to using brand_can whenever possible
  const brand_can = computed(() => identityStore.brand_can);
  const user_can = computed(() => identityStore.user_can);
  const identityBrandsById = computed(() => identityStore.identityBrandsById);
  const isSuperAdmin = computed(() => identityStore.isSuperAdmin);
  const canAccessOrganic = computed(() => identityStore.canAccessOrganic);
  const canAccessVision = computed(() => identityStore.canAccessVision);
  const canAccessStoriesSwipeups = computed(() => identityStore.canAccessStoriesSwipeups);
  const brandCanAccessScheduler = computed(() => identityStore.brandCanAccessScheduler);
  const canAccessSchedulerPlatform = computed(() => identityStore.canAccessSchedulerPlatform);
  const canAccessAutoPublish = computed(() => identityStore.canAccessAutoPublish);
  const brandCanAccessIndustryBenchmarks = computed(
    () => identityStore.brandCanAccessIndustryBenchmarks,
  );
  const hasBoardsAccess = computed(() => identityStore.hasBoardsAccess);
  const currentBrandRole = computed(() => identityStore.currentBrandRole);
  const currentPlanType = computed(() => identityStore.currentPlanType);
  const isCurrentBrandRoleAdmin = computed(() => identityStore.isCurrentBrandRoleAdmin);
  const identityBrandIds = computed(() => identityStore.identityBrandIds);
  const canAccessMultipleBrands = computed(() => identityStore.canAccessMultipleBrands);

  function guard(
    permissionObject: PermissionObject | PermissionObject[],
    customBrand?: Brand | null,
  ): boolean {
    return identityStore.guard(permissionObject, customBrand);
  }

  async function setCurrentBrand({ brandLabel }: { brandLabel: string }): Promise<void> {
    return identityStore.setCurrentBrand({ brandLabel });
  }

  function setIdentity({ identity: newIdentity }: { identity: RawIdentity }): void {
    // Check if the user is the 'dh-integrations' API user, this should never happen
    // We have had reports of this so log them out for now so they can login with their actually account
    if (newIdentity?.first_name === 'dh-integrations') {
      logger.error('dh-integrations user logged in, logging out...');
      window.location.assign('/logout');
    }
    return identityStore.setIdentity({ identity: newIdentity });
  }

  function setCurrentBrandByAttributes({ brand }: { brand: Brand }): Promise<void> {
    return identityStore.setCurrentBrandByAttributes({ brand });
  }

  function updateIdentity({ data }: { data: Record<string, unknown> }): void {
    return identityStore.updateIdentity({ data });
  }

  function filterBrands(search: string | null): Brand[] {
    return identityStore.filterBrands(search);
  }

  function checkIfBrandPermissionsExist(
    permissions: PermissionObject[] | null,
    brands?: Record<string, Brand> | Brand[] | null,
  ): boolean {
    return identityStore.checkIfBrandPermissionsExist(permissions, brands);
  }

  function checkIfOrgPermissionsExist(
    permissions: PermissionObject[] | null,
    brands: Record<string, Brand> | Brand[],
  ): boolean {
    return identityStore.checkIfOrgPermissionsExist(permissions, brands);
  }

  function checkIfUserPermissionsExist(
    permissions: PermissionObject[] | null,
    brands?: Record<string, Brand> | null,
  ): boolean {
    return identityStore.checkIfUserPermissionsExist(permissions, brands);
  }
  /** ************************************************************ */

  const brandUser = ref<BrandUserExpanded | null>(null);
  const brandUsers = ref<BrandUser[]>([]);
  const brandUsersNext = ref<string | null>(null);
  const brandUsersOffset = ref(0);
  const pending = ref<AuthPendingState>({
    brandUsers: false,
    export: false,
    addTagsToMultipleBrands: false,
    identity: false,
    setCompanyCustomerInfo: false,
    resetUserPassword: false,
    changeUserPassword: false,
  });
  const userListForAllBrandsCSV = ref<unknown>(null);
  const brandAccessibleUsers = ref<BrandUser[] | null>(null);
  const samlIdP = ref<string | null>(null);
  const oauthVersion = ref(browserStorageGetItem('oauthVersion'));
  const oauthState = ref(browserStorageGetItem('oauthState'));
  const oauthResourceOwnerKey = ref(browserStorageGetItem('oauthResourceOwnerKey'));
  const oauthResourceOwnerSecret = ref(browserStorageGetItem('oauthResourceOwnerSecret'));
  const oauthNext = ref(browserStorageGetItem('oauthNext'));
  const oauthDeniedNext = ref(browserStorageGetItem('oauthDeniedNext'));
  const oauthBrand = ref(browserStorageGetItem('oauthBrand'));
  const oauthBrandToken = ref(browserStorageGetItem('oauthBrandToken'));
  const oauthConnectionId = ref(browserStorageGetItem('oauthConnectionId'));
  const oauthInvitedByBrandId = ref(browserStorageGetItem('oauthInvitedByBrandId'));
  const oauthSourceCreatorId = ref(browserStorageGetItem('oauthSourceCreatorId'));
  const oauthCreatorHandleName = ref(browserStorageGetItem('oauthInfluencer'));

  const isProcessingLogin = ref(false);
  const loginErrorMessage = ref<string | null>(null);

  const hasAttemptedIdentification = ref(false);
  const isRouteUnauthorized = ref(false); // user level permission check
  const brandTags = ref<BrandTag[]>([]);
  const filteredAccessibleBrandTags = ref<RawBrandTag[]>([]);
  const salesTerritory = ref<string | null>(null);
  const salesRegion = ref<string | null>(null);

  function getBrandTagsById(currentOrgOnly = false): Record<number, BrandTagById> {
    if (!identityStore.identity) {
      return {};
    }
    const tagBrandMapping: Record<number, BrandTagById> = {};
    const identityBrands = Object.values(identityStore.identity?.brands ?? {});
    let brands: Brand[] = [];
    if (currentOrgOnly) {
      brands = identityBrands.filter(
        (brand) => brand.organizationId === currentBrand.value?.organizationId,
      );
    } else {
      brands = identityBrands;
    }

    brands.forEach((brand) => {
      const tags = brand?.tags ?? [];

      tags.forEach((tag) => {
        const brandCountFromTag = brandTags.value.find(
          (brandTag) => tag.id === brandTag.id,
        )?.brandCount;
        if (!tagBrandMapping?.[tag.id]) {
          tagBrandMapping[tag.id] = {
            id: tag.id,
            name: tag.name,
            color: tag.color,
            brands: [],
            brandCount: brandCountFromTag,
          };
        }
        tagBrandMapping[tag.id].brands.push({
          name: brand.name,
          avatarUrl: brand.avatarUrl,
          id: brand.id,
          label: brand.label,
        });
      });
    });
    Object.values(tagBrandMapping).forEach((tag) => {
      tag.inaccessibleBrandsCount = (tag.brandCount ?? 0) - tag.brands.length;
    });
    return tagBrandMapping;
  }

  const brandTagsById = computed(() => getBrandTagsById());

  function hasPlan(incomingPlansToCheck: string | string[]): boolean {
    const plansToCheck = Array.isArray(incomingPlansToCheck)
      ? incomingPlansToCheck
      : [incomingPlansToCheck];
    return plansToCheck.some((plan) => isSameCustomerPlan(plan, currentPlanType));
  }

  async function getBrandUsers({
    brandId,
    email,
    includeRestricted = false, // true = include dash social users etc.
    holdMutation = false,
    limit,
  }: {
    brandId: number;
    email?: string;
    includeRestricted?: boolean;
    holdMutation?: boolean;
    limit?: number;
  }) {
    pending.value.brandUsers = true;
    try {
      const response = await AuthAPI.getBrandUsers({
        brandId,
        email,
        includeRestricted,
        limit,
        sort: 'invitation_status,is_admin,first_name,last_name',
      });
      const payload = response?.data;

      if (!holdMutation) {
        brandUsers.value = payload?.data ?? [];
        brandUsersNext.value = (payload?.paging as { next?: string })?.next ?? null;
      }

      return response;
    } finally {
      pending.value.brandUsers = false;
    }
  }

  function clearBrandUsers() {
    brandUsers.value = [];
    brandUsersOffset.value = 0;
  }

  async function getPaginatedBrandUsers({
    brandId,
    limit = 20,
    search,
    clear,
  }: {
    brandId: number;
    limit?: number;
    search?: string;
    clear?: boolean;
  }) {
    pending.value.brandUsers = true;
    try {
      if (clear) {
        clearBrandUsers();
      }
      if (brandUsers.value.length === 0 || brandUsersNext.value || clear) {
        const response = await AuthAPI.getBrandUsers({
          brandId,
          limit,
          offset: clear ? 0 : brandUsersOffset.value,
          search,
          includeRestricted: false,
          sort: 'invitation_status,is_admin,first_name,last_name',
        });
        const payload = response?.data;

        brandUsers.value = clear
          ? (payload.data ?? [])
          : [...brandUsers.value, ...(payload.data ?? [])];
        brandUsersNext.value = (payload.paging as { next?: string })?.next ?? null;
        brandUsersOffset.value += limit;
      }
    } finally {
      pending.value.brandUsers = false;
    }
  }
  function canDeleteBrandTag(tagId: number): boolean {
    const tag = brandTagsById.value[tagId];
    return tag?.inaccessibleBrandsCount === 0;
  }
  async function getBrandUser({ brandId, userId }: { brandId: number; userId: number }) {
    const response = await AuthAPI.getBrandUser({ brandId, userId });
    const user = response.data;
    brandUser.value = {
      ...user,
      permission: flattenBrandUserPermissions(user.permission) as BrandUserExpanded['permission'],
    };
  }
  async function createBrandUser({
    brandId,
    email,
    permissions,
    role,
  }: {
    brandId: number;
    email: string;
    permissions: Record<string, unknown>;
    role: string;
  }) {
    return AuthAPI.createBrandUser({ brandId, email, permissions, role });
  }
  async function updateBrandUser({
    brandId,
    userId,
    role,
    permissions,
  }: {
    brandId: number;
    userId: number;
    role: string;
    permissions: Record<string, unknown>;
  }) {
    return AuthAPI.updateBrandUser({ brandId, userId, role, permissions });
  }
  async function removeBrandUser({ brandId, userId }: { brandId: number; userId: number }) {
    return AuthAPI.removeBrandUser({ brandId, userId });
  }
  async function resendBrandUserInvitation({
    brandId,
    userId,
  }: {
    brandId: number;
    userId: number;
  }) {
    return AuthAPI.resendBrandUserInvitation({ brandId, userId });
  }
  async function generateApiKey({ brandId, label }: { brandId: number; label: string }) {
    return AuthAPI.generateApiKey({ brandId, label });
  }
  async function getApiKeysForBrand({ brandId }: { brandId: number }) {
    return AuthAPI.getApiKeysForBrand({ brandId });
  }
  function clearBrandUser() {
    brandUser.value = null;
  }

  async function getUserListForAllBrandsCSV({ userId }: { userId: number }) {
    pending.value.export = true;
    try {
      const response = await AuthAPI.getBrandUserExportUserList({
        userId,
      });
      userListForAllBrandsCSV.value = response?.data;
    } catch (error) {
      pending.value.export = false;
      throw error;
    }
  }
  function receivedCSV() {
    pending.value.export = false;
  }
  async function getBrandAccessibleUsers({ brandId }: { brandId: number }) {
    try {
      const response = await AuthAPI.getBrandUsers({
        brandId,
        sort: 'first_name,last_name',
      });
      const payload = response?.data;
      brandAccessibleUsers.value = payload?.data ?? [];
      return response;
    } catch (error) {
      if (getHttpErrorStatus(error) === 401) {
        logger.error(
          `auth getBrandAccessibleUsers request returned authentication error: ${getHttpErrorDescription(error)}`,
          {},
          error,
        );
        return null;
      }
      throw error;
    }
  }

  function clearBrandAccessibleUsers() {
    brandAccessibleUsers.value = null;
  }

  function submitFormPost(path: string, params: Record<string, string>, method = 'post') {
    const form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    Object.keys(params).forEach((key) => {
      const hiddenField = document.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', key);
      hiddenField.setAttribute('value', params[key]);

      form.appendChild(hiddenField);
    });

    document.body.appendChild(form);
    form.submit();
  }

  async function createSamlAssertion({
    samlRequest,
    samlRelayState,
  }: {
    samlRequest: string;
    samlRelayState: string;
  }) {
    const response = await AuthAPI.postSamlAssertion({
      samlRequest,
      samlRelayState,
    });
    const {
      destination,
      saml_response: SAMLResponse,
      relay_state: RelayState,
    } = response?.data ?? {};
    submitFormPost(destination, { SAMLResponse, RelayState });
  }

  function getUniqueTags(selfResponse: { data?: RawIdentity }): RawBrandTag[] {
    const brands = selfResponse?.data?.brands;
    if (!brands) return [];

    const tagMap = new Map<
      number,
      RawBrandTag & {
        brands: Array<{ avatarUrl?: string; id: number; label: string; name: string }>;
      }
    >();

    Object.values(brands).forEach((brand) => {
      const brandInfo = {
        avatarUrl: brand.avatar_url,
        id: brand.id,
        label: brand.label,
        name: brand.name,
      };

      if (!Array.isArray(brand.tags)) return;

      brand.tags.forEach((tag) => {
        if (tagMap.has(tag.id)) {
          tagMap.get(tag.id)!.brands.push(brandInfo);
        } else {
          tagMap.set(tag.id, {
            brand_count: tag.brand_count,
            color: tag.color,
            id: tag.id,
            name: tag.name,
            organization_id: tag.organization_id,
            brands: [brandInfo],
          });
        }
      });
    });

    return Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  async function identify({ backgroundSecondaryData = false } = {}) {
    loginErrorMessage.value = null;
    hasAttemptedIdentification.value = true;
    pending.value.identity = true;
    let selfResponse;
    let secondaryDataReady = Promise.resolve();

    try {
      selfResponse = await AuthAPI.getSelf();

      // Set identity early — makes isLoggedIn true, triggers LD init,
      // enables route navigation while secondary data loads
      setIdentity({ identity: selfResponse?.data });
      filteredAccessibleBrandTags.value = getUniqueTags(selfResponse);

      const brandCount = Object.keys(selfResponse?.data?.brands ?? {}).length;
      const inactiveBrandCount = Object.keys(selfResponse?.data?.inactive_brands ?? {}).length;
      if (brandCount === 0 && inactiveBrandCount === 0 && !selfResponse?.data?.is_shared) {
        logger.error('Self returned no brands', {
          userId: selfResponse?.data?.id,
          email: selfResponse?.data?.email,
        });
      }

      if (selfResponse && !selfResponse.data?.is_shared) {
        secondaryDataReady = Promise.all([
          AuthAPI.getSelfSubscriptions(),
          AuthAPI.getSelfActionMetadata(),
        ]).then(([subsResponse, metadataResponse]) => {
          identityStore.setActionMetadata(metadataResponse?.data);
          identityStore.setSubscriptions(subsResponse?.data);
        });

        if (backgroundSecondaryData) {
          secondaryDataReady.catch((error) => {
            logger.error('Failed to load secondary identity data in background', {}, error);
          });
        } else {
          await secondaryDataReady;
        }
      }
    } finally {
      pending.value.identity = false;
    }

    return { secondaryDataReady };
  }

  async function refreshSelfActionMetadata() {
    let res;
    try {
      res = await AuthAPI.getSelfActionMetadata();
    } catch (error) {
      logger.error('error fetching action metadata for self: ', {}, error);
    }
    identityStore.setActionMetadata(res?.data ?? null);
  }

  async function login({
    email,
    password,
    otpCode,
  }: {
    email?: string;
    password?: string;
    otpCode?: string;
  }) {
    isProcessingLogin.value = true;
    loginErrorMessage.value = null;
    identityStore.clearIdentity();

    try {
      const response = await AuthAPI.createToken({
        email,
        password,
        otpCode,
      });
      const payload = response?.data;
      const newToken = payload?.token;
      if (!newToken) throw new Error('Token missing from login response');
      tokenStore.setToken({ token: newToken });
      shouldShowLoginAnimation.value = true;

      const { secondaryDataReady } = await identify({ backgroundSecondaryData: true });

      secondaryDataReady
        .then(() => {
          try {
            track.loggedOn({
              method: LoginMethodLabel.email_password,
              enforcedLoginMethod:
                LoginMethodLabel[
                  identityStore.identity!.login_configuration!
                    .login_method as keyof typeof LoginMethodLabel
                ],
            });
          } catch (error) {
            logger.error('Failed to track loggedOn event after login', {}, error);
          }
        })
        .catch((error) => {
          logger.error('Failed to load secondary identity data after login', {}, error);
        });
    } catch (error) {
      shouldShowLoginAnimation.value = false;
      const otpError = oneTimePasswordStore.handleLoginError({ error, email, password });
      if (!otpError) {
        loginErrorMessage.value =
          getHttpErrorDescription(error) ||
          'An error occurred during login. Please try again later.';
      }
      throw error;
    } finally {
      isProcessingLogin.value = false;
    }
  }

  async function exchangeSsoToken({ ssoToken }: { ssoToken: string }) {
    try {
      const response = await AuthAPI.createToken({ ssoToken });
      const payload = response?.data;
      const newToken = payload?.token;
      if (!newToken) throw new Error('Token missing from SSO response');
      tokenStore.setToken({ token: newToken });
      shouldShowLoginAnimation.value = true;

      const { secondaryDataReady } = await identify({ backgroundSecondaryData: true });

      secondaryDataReady
        .then(() => {
          try {
            track.loggedOn({
              method: LoginMethodLabel.sso,
              enforcedLoginMethod:
                LoginMethodLabel[
                  identityStore.identity!.login_configuration!
                    .login_method as keyof typeof LoginMethodLabel
                ],
            });
          } catch (error) {
            logger.error('Failed to track loggedOn event after SSO login', {}, error);
          }
        })
        .catch((error) => {
          logger.error('Failed to load secondary identity data after SSO login', {}, error);
        });
    } catch (error) {
      shouldShowLoginAnimation.value = false;
      loginErrorMessage.value = 'An error occurred during SSO login. Please try again later.';
      throw error;
    }
  }

  async function logout() {
    identityStore.clearCurrentBrand();
    identityStore.clearIdentity();
    impersonatorStore.clearImpersonatedUser();
    isProcessingLogin.value = false;
    loginErrorMessage.value = null;

    // for hiding billing and plans
    const hostname = window.location.hostname;
    if (hostname.endsWith('dashsocial.com')) {
      document.cookie =
        'HIDE_BILLING=; domain=.dashsocial.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure';
    }

    await AuthAPI.destroyToken();
    tokenStore.clearToken();
    // Leaving this in for now to ensure all tokens are removed from local storage
    browserStorageRemoveItem('token');
  }

  function reset() {
    identityStore.clearIdentity();
    tokenStore.reset();
    hasAttemptedIdentification.value = false;
  }

  function setRouteUnauthorized({ unauthorized }: { unauthorized: boolean }) {
    isRouteUnauthorized.value = unauthorized;
  }

  function clearLoginAnimation() {
    shouldShowLoginAnimation.value = false;
  }

  // check permission at user level (looking at identity.permissions)
  function checkRouteAccess({ route }: { route: RouteLocationNormalized }) {
    const unauthorized = !guardRoute(identityStore.identity, identityStore.currentBrand, route);
    setRouteUnauthorized({ unauthorized });
  }

  async function checkForIdentity() {
    try {
      await identify({ backgroundSecondaryData: true });
    } catch {
      setRouteUnauthorized({ unauthorized: true });
      return false;
    }
    return true;
  }

  async function loginByTokenAndBrandLabel({
    token: loginToken,
    brand,
  }: { token?: string; brand?: string } = {}) {
    if (loginToken) {
      isProcessingLogin.value = false;
      loginErrorMessage.value = null;
      tokenStore.setToken({ token: loginToken });

      const response = await AuthAPI.getSelf();
      const newIdentity = response?.data;

      loginErrorMessage.value = null;
      identityStore.setIdentity({ identity: newIdentity });
    } else {
      await identify();
    }
    const identityBrandLabels = identityStore.identity?.brands
      ? Object.keys(identityStore.identity.brands)
      : [];
    if (identityBrandLabels.length > 0) {
      if (brand && identityBrandLabels.indexOf(brand) > 0) {
        void setCurrentBrand({ brandLabel: brand });
      } else {
        const firstBrandLabel = identityBrandLabels[0];
        void setCurrentBrand({ brandLabel: firstBrandLabel });
      }
    }
  }

  async function resetUserPassword({
    userId,
    hash,
    password,
  }: {
    userId: number;
    hash: string;
    password: string;
  }) {
    pending.value.resetUserPassword = true;
    try {
      await AuthAPI.resetUserPassword({ userId, hash, password });
    } finally {
      pending.value.resetUserPassword = false;
    }
  }

  async function changeUserPassword({
    userId,
    password,
    oldPassword,
  }: {
    userId: number;
    password: string;
    oldPassword: string;
  }) {
    pending.value.changeUserPassword = true;
    try {
      await AuthAPI.changeUserPassword({ userId, password, oldPassword });
    } finally {
      pending.value.changeUserPassword = false;
    }
  }

  async function startOAuth({
    router,
    provider,
    next,
    brandId,
  }: {
    router: Router;
    provider: string;
    next: string;
    brandId: number;
  }) {
    isProcessingLogin.value = true;
    loginErrorMessage.value = null;
    browserStorageSetItem('startOauthFrom', router.currentRoute.value?.fullPath);

    const redirectUri = buildOAuthRedirectUri({ provider, router });

    try {
      const response = await AuthAPI.postOAuth({
        provider,
        redirectUri,
        brandId,
      });
      const {
        oauth_version: incomingOauthVersion,
        authorization_url: authUrl,
        state: incomingOauthState,
        resource_owner_key: resourceOwnerKey,
        resource_owner_secret: resourceOwnerSecret,
      } = response.data;
      browserStorageSetItem('oauthNext', next);
      browserStorageSetItem('oauthVersion', incomingOauthVersion);
      if (incomingOauthVersion === 1) {
        browserStorageSetItem('oauthResourceOwnerKey', resourceOwnerKey);
        browserStorageSetItem('oauthResourceOwnerSecret', resourceOwnerSecret);
      } else {
        browserStorageSetItem('oauthState', incomingOauthState);
      }

      window.location.assign(authUrl);
    } catch (error) {
      if (isHttpError(error)) {
        loginErrorMessage.value = getHttpErrorDescription(error) ?? '';
      } else {
        throw error;
      }
    } finally {
      isProcessingLogin.value = false;
    }
  }

  function buildOAuthRedirectUri({ provider, router }: { provider: string; router: Router }) {
    // We plan to drop our X enterprise app and don't want X to correlate our other
    // apps back to Dash, so in production we route the twitter_oauth2 callback
    // through a separate domain to hide the connection.
    const origin =
      provider === TWITTER_OAUTH2_PROVIDER && env.isProduction
        ? TWITTER_OAUTH2_PRODUCTION_CALLBACK_ORIGIN
        : window.location.origin;

    return `${origin}${router.resolve({ name: 'auth.callback', params: { provider } }).href}`;
  }

  function cleanupOAuthState() {
    browserStorageRemoveItem('oauthVersion');
    browserStorageRemoveItem('oauthBrand');
    browserStorageRemoveItem('oauthBrandToken');
    browserStorageRemoveItem('oauthConnectionId');
    browserStorageRemoveItem('oauthInvitedByBrandId');
    browserStorageRemoveItem('oauthInfluencer');
    browserStorageRemoveItem('oauthSourceCreatorId');
    browserStorageRemoveItem('oauthNext');
    browserStorageRemoveItem('oauthDeniedNext');
    browserStorageRemoveItem('oauthState');
    browserStorageRemoveItem('oauthResourceOwnerKey');
    browserStorageRemoveItem('oauthResourceOwnerSecret');
  }

  async function processOAuthCallbackLogin({ oauth }: { oauth: unknown }) {
    isProcessingLogin.value = true;
    loginErrorMessage.value = null;
    try {
      const response = await AuthAPI.createToken({ oauth });
      const payload = response?.data;
      const newToken = payload?.token;
      if (!newToken) throw new Error('Token missing from OAuth response');
      tokenStore.setToken({ token: newToken });
      shouldShowLoginAnimation.value = true;

      const { secondaryDataReady } = await identify({ backgroundSecondaryData: true });

      secondaryDataReady
        .then(() => {
          try {
            track.loggedOn({
              method: LoginMethodLabel.oauth,
              enforcedLoginMethod:
                LoginMethodLabel?.[
                  identityStore.identity?.login_configuration
                    ?.login_method as keyof typeof LoginMethodLabel
                ],
            });
          } catch (error) {
            logger.error('Failed to track loggedOn event after OAuth login', {}, error);
          }
        })
        .catch((error) => {
          logger.error('Failed to load secondary identity data after OAuth login', {}, error);
        });
    } catch (error) {
      shouldShowLoginAnimation.value = false;
      logger.error('auth /tokens request returned error: ', {}, error);
      loginErrorMessage.value = getHttpErrorDescription(error) || defaultErrorMessage;
      throw error;
    } finally {
      isProcessingLogin.value = false;
      cleanupOAuthState();
    }
  }

  async function getBrands(params: Record<string, unknown>) {
    try {
      const response = await AuthAPI.getBrands({ params });
      return response.data;
    } catch (error) {
      const status = getHttpErrorStatus(error);
      if (status === 401 || status === 403) {
        logger.error(
          `auth /brands request authentication error: ${getHttpErrorDescription(error)}`,
          {},
          error,
        );
        return [];
      }
      throw error;
    }
  }

  function canManageBrandTags(identityBrands: Record<string, Brand> | Brand[]): boolean {
    const userOrganizationPermissions = identity.value?.organization_user_permissions;
    return Object.values(identityBrands).some((brand) => {
      const orgPermissions = userOrganizationPermissions?.[brand.organizationId!]?.tags;
      return orgPermissions?.can_manage_brand_tags === true && brand.id === currentBrand.value?.id;
    });
  }

  function canManageCompetitorTags(
    userOrganizationPermissions: Record<number, Record<string, Record<string, boolean>>>,
    brand: Brand,
  ): boolean {
    return (
      userOrganizationPermissions[brand.organizationId!]?.tags?.can_manage_competitor_tags ?? false
    );
  }
  async function fetchBrandTags() {
    const response = await AuthAPI.getBrandTags({
      organizationId: identityStore.currentBrand!.organizationId!,
    });
    brandTags.value = response.data;
  }
  async function getBrandTagsByOrganizationId(orgId: number) {
    const response = await AuthAPI.getBrandTags({ organizationId: orgId });
    return response?.data;
  }
  async function createBrandTag({ name, color }: { name: string; color: string }) {
    const response = await AuthAPI.createBrandTag({
      organizationId: identityStore.currentBrand!.organizationId!,
      name,
      color,
    });
    const newTag = response?.data;
    brandTags.value.push(newTag);
    return newTag;
  }
  async function addTagsToMultipleBrands({
    brandIds,
    selectedTags,
  }: {
    brandIds: number[];
    selectedTags: BrandTag[];
  }) {
    pending.value.addTagsToMultipleBrands = true;
    const brandTagIds = selectedTags.map((tag) => tag.id);
    try {
      await AuthAPI.createBrandTagAssociations({
        organizationId: identityStore.currentBrand!.organizationId!,
        brandIds,
        brandTagIds,
      });
    } finally {
      pending.value.addTagsToMultipleBrands = false;
    }

    const brands = Object.values(identityStore.identity!.brands!).filter((brand) =>
      brandIds.includes(brand.id),
    );
    brands.forEach((brand) => {
      brand.tags = uniqBy([...selectedTags, ...(brand.tags ?? [])], 'id');
      identityStore.identity!.brands![brand.label] = brand;
    });
  }
  async function removeTagFromBrand({ brandId, tag }: { brandId: number; tag: BrandTag }) {
    try {
      await AuthAPI.removeTagFromBrand({
        organizationId: tag.organizationId!,
        brandId,
        brandTagId: tag.id,
      });
    } catch (error) {
      if (getHttpErrorStatus(error) !== 404) {
        throw error;
      }
    }

    const brand = Object.values(identityStore.identity!.brands!).find(({ id }) => id === brandId);
    if (brand) {
      brand.tags = (brand.tags ?? []).filter(({ id }) => id !== tag.id);
    }
  }
  async function deleteBrandTag({ tag }: { tag: BrandTag }) {
    try {
      await AuthAPI.deleteBrandTag({ organizationId: tag.organizationId!, brandTagId: tag.id });
    } catch (error) {
      if (getHttpErrorStatus(error) !== 404) {
        throw error;
      }
    }
  }

  function getBrandNames(brandIds: number[]): string[] {
    const brandNames: string[] = [];
    const brandsById = identityBrandsById.value;
    brandIds.forEach((brandId) => {
      if (brandsById[brandId]) {
        brandNames.push(brandsById[brandId].name);
      }
    });
    return brandNames;
  }

  async function setCompanyCustomerInfo() {
    if (currentBrand.value?.organization?.company?.id === undefined) {
      salesTerritory.value = null;
      salesRegion.value = null;
    } else
      try {
        pending.value.setCompanyCustomerInfo = true;
        const { data } = await AuthAPI.getCompanyCustomerInformation(
          currentBrand.value.organization?.company?.id,
        );
        salesTerritory.value = data.salesTerritory ?? null;
        salesRegion.value = data.country ?? null;
      } catch (e) {
        logger.error(
          `auth /companies/${currentBrand.value?.organization?.company?.id}/customer_information error description: ${getHttpErrorDescription(e) ?? ''}`,
          {},
          e,
        );
      } finally {
        pending.value.setCompanyCustomerInfo = false;
      }
  }

  return {
    brandUser,
    brandUsers,
    brandUsersNext,
    brandUsersOffset,
    pending,
    userListForAllBrandsCSV,
    brandAccessibleUsers,
    samlIdP,
    oauthVersion,
    oauthState,
    oauthResourceOwnerKey,
    oauthResourceOwnerSecret,
    oauthNext,
    oauthDeniedNext,
    oauthBrand,
    oauthBrandToken,
    oauthConnectionId,
    oauthInvitedByBrandId,
    oauthSourceCreatorId,
    oauthCreatorHandleName,
    identity,
    isProcessingLogin,
    loginErrorMessage,
    currentBrandLabel,
    hasAttemptedIdentification,
    isLoggedIn,
    shouldShowLoginAnimation,
    clearLoginAnimation,
    isRouteUnauthorized,
    brandTags,
    salesRegion,
    salesTerritory,
    identityBrandsById,
    isSuperAdmin,
    currentBrand,
    canAccessOrganic,
    canAccessVision,
    canAccessStoriesSwipeups,
    brandCanAccessScheduler,
    canAccessSchedulerPlatform,
    canAccessAutoPublish,
    brandCanAccessIndustryBenchmarks,
    brand_can,
    user_can,
    hasBoardsAccess,
    currentBrandRole,
    currentPlanType,
    isCurrentBrandRoleAdmin,
    identityBrandIds,
    canOnlyAccessInactiveBrand,
    canAccessMultipleBrands,
    brandTagsById,
    getBrandNames,
    guard,
    hasPlan,
    getBrandUsers,
    getPaginatedBrandUsers,
    getBrandUser,
    createBrandUser,
    updateBrandUser,
    removeBrandUser,
    resendBrandUserInvitation,
    generateApiKey,
    getApiKeysForBrand,
    clearBrandUser,
    clearBrandUsers,
    getUserListForAllBrandsCSV,
    receivedCSV,
    getBrandAccessibleUsers,
    clearBrandAccessibleUsers,
    createSamlAssertion,
    setIdentity,
    setCurrentBrand,
    login,
    exchangeSsoToken,
    logout,
    reset,
    checkRouteAccess,
    setCurrentBrandByAttributes,
    setCompanyCustomerInfo,
    identify,
    checkForIdentity,
    loginByTokenAndBrandLabel,
    resetUserPassword,
    changeUserPassword,
    setRouteUnauthorized,
    startOAuth,
    buildOAuthRedirectUri,
    processOAuthCallbackLogin,
    cleanupOAuthState,
    updateIdentity,
    getBrands,
    checkIfBrandPermissionsExist,
    checkIfOrgPermissionsExist,
    checkIfUserPermissionsExist,
    fetchBrandTags,
    createBrandTag,
    addTagsToMultipleBrands,
    removeTagFromBrand,
    deleteBrandTag,
    filterBrands,
    canManageBrandTags,
    canManageCompetitorTags,
    canDeleteBrandTag,
    refreshSelfActionMetadata,
    getBrandTagsById,
    getBrandTagsByOrganizationId,
    filteredAccessibleBrandTags,
  };
});
